import { FAIcon, type RadioOption } from '@/components'
import {
  AbsenceStatusCombobox,
  AbsenceTypeCombobox,
  Card,
  DatePicker,
  Field,
  FieldLabel,
  LeaderGroupCombobox,
  MonthSelect,
  Radios,
  useHideBasedOnLeaderGroup,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import type { UseLeaderGroupsSuspenseReturn } from '@/utils'
import { PERMISSIONS, usePermissions } from '@/utils'
import { type UpdateFormFn, useTranslate } from '@ur-solutions/react-hooks'
import { Activity, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import z from 'zod'
import { absenceRequestStatuses } from '../absence-status.mapping'
import { absenceViewValues } from '../consts'
import type { AbsenceFilterForm } from '../types'

export const AbsencesFiltersSchema = z.object({
  view: z.enum(absenceViewValues),
  users: z.string().array(),
  absenceType: z.string(),
  requestStatus: z.enum(absenceRequestStatuses).or(z.literal('')),
  dateRange: z.object({
    from: z.date(),
    to: z.date(),
  }),
  byCreatedDate: z.boolean(),
  month: z.date(),
  leaderGroup: z.string(),
})

export type AbsencesFiltersForm = z.infer<typeof AbsencesFiltersSchema>

interface Props {
  filter: AbsenceFilterForm
  updateForm: UpdateFormFn<AbsenceFilterForm>
  leaderGroups: UseLeaderGroupsSuspenseReturn
}

export function AbsencesFilters({ filter, updateForm, leaderGroups }: Props) {
  const translations = useTranslate({
    filter: 'common.filter',
    users: 'absences.filters.users',
    absenceType: 'absences.filters.absence-type',
    requestStatus: 'absences.filters.request-status',
    dateRange: 'absences.filters.date-range',
    month: 'absences.filters.month',
    leaderGroup: 'absences.filters.leader-group',

    select: 'common.select-ellipsis',

    filtering: 'absences.filters.date-filtering',
    byCreatedDate: 'absences.filters.by-created-date',
    byStartDate: 'absences.filters.by-start-date',
  })

  const view = filter.view
  const leaderGroup = filter.leaderGroup ?? ''

  const hideUserItem = useHideBasedOnLeaderGroup(leaderGroup, leaderGroups)

  const { hasPermissions } = usePermissions()
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    false
  )
  const [collapsed, setCollapsed] = useState(isMobileOnly)
  const showFilters = !isMobileOnly || !collapsed

  const byCreatedOptions: RadioOption<'created' | 'started'>[] = [
    {
      value: 'started',
      label: translations.byStartDate,
    },
    {
      value: 'created',
      label: translations.byCreatedDate,
    },
  ]

  return (
    <Card className={cn('mb-4 p-0')} contentClassName="p-0">
      {isMobileOnly && (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-sm font-semibold text-stone-600"
          aria-expanded={showFilters}
          onClick={() => setCollapsed(value => !value)}
        >
          {translations.filter}
          <FAIcon
            className={cn('transition-transform', showFilters && 'rotate-180')}
            icon="chevron-down"
          />
        </button>
      )}

      {showFilters && (
        <form
          className={cn(
            'grid p-4 gap-4',
            'md:flex md:flex-wrap md:justify-start'
          )}
        >
          <Activity mode={canViewOthersAbsence ? 'visible' : 'hidden'}>
            <Field>
              <FieldLabel>{translations.users}</FieldLabel>
              <UserCombobox
                value={filter.users ?? []}
                className="max-w-[350px]"
                mode="multiple"
                hideItem={hideUserItem}
                placeholder={translations.select}
                onChange={value => {
                  updateForm({ users: value })
                }}
              />
            </Field>
          </Activity>

          <Activity
            mode={
              canViewOthersAbsence && leaderGroups.hasAccess ? 'visible' : 'hidden'
            }
          >
            <Field>
              <FieldLabel>{translations.leaderGroup}</FieldLabel>
              <LeaderGroupCombobox
                value={filter.leaderGroup ?? ''}
                mode="single"
                module="Absences"
                clearable
                placeholder={translations.select}
                onChange={value => {
                  updateForm({
                    leaderGroup: value || null,
                    users: [],
                  })
                }}
              />
            </Field>
          </Activity>

          <Field>
            <FieldLabel>{translations.absenceType}</FieldLabel>
            <AbsenceTypeCombobox
              value={filter.absenceType}
              mode="single"
              placeholder={translations.select}
              onChange={value => updateForm({ absenceType: value || '' })}
            />
          </Field>

          <Activity
            mode={view === 'requests' || view === 'gantt' ? 'hidden' : 'visible'}
          >
            <Field>
              <FieldLabel>{translations.requestStatus}</FieldLabel>
              <AbsenceStatusCombobox
                value={filter.requestStatus ?? ''}
                mode="single"
                placeholder={translations.select}
                onChange={value =>
                  updateForm({ requestStatus: value || undefined })
                }
              />
            </Field>
          </Activity>

          <Activity
            mode={view === 'table' || view == 'gantt' ? 'visible' : 'hidden'}
          >
            <Field>
              <FieldLabel>{translations.dateRange}</FieldLabel>
              <DatePicker
                value={filter.dateRange}
                mode="range"
                format="PP"
                footer={
                  <div className="flex justify-center gap-4 mt-4">
                    <FieldLabel>{translations.filtering}</FieldLabel>
                    <Radios
                      value={filter.byCreatedDate ? 'created' : 'started'}
                      options={byCreatedOptions}
                      orientation="horizontal"
                      onChange={value =>
                        updateForm({ byCreatedDate: value === 'created' })
                      }
                    />
                  </div>
                }
                onChange={value => {
                  if (!value?.from || !value.to) return
                  updateForm({
                    dateRange: {
                      from: value.from,
                      to: value.to,
                    },
                  })
                }}
              />
            </Field>
          </Activity>

          <Activity mode={view === 'calendar' ? 'visible' : 'hidden'}>
            <Field>
              <FieldLabel>{translations.month}</FieldLabel>
              <MonthSelect
                value={filter.month}
                onChange={value => updateForm({ month: value })}
              />
            </Field>
          </Activity>
        </form>
      )}
    </Card>
  )
}
